/**
 * T114-F5-i: Batch Apply Rule Dialog
 * Select a rule from existing rules and preview/apply to selected transactions.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye, Wand2, ArrowRight } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchTransactionRules } from '@/features/transactionRules/transactionRulesSlice';
import { batchApplyRule } from '@/features/transactions/transactionsSlice';
import { useToast } from '@/hooks/use-toast';
import type { BatchOperationResult } from '@/lib/types';

interface BatchApplyRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSuccess: () => void;
}

export function BatchApplyRuleDialog({
  open,
  onOpenChange,
  selectedIds,
  onSuccess,
}: BatchApplyRuleDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { rules } = useAppSelector((state) => state.transactionRules);

  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [preview, setPreview] = useState<BatchOperationResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (open && rules.length === 0) {
      dispatch(fetchTransactionRules());
    }
  }, [open, rules.length, dispatch]);

  const selectedRule = rules.find((r) => r._id === selectedRuleId);

  const handlePreview = async () => {
    if (!selectedRuleId) return;
    setIsPreviewLoading(true);
    try {
      const result = await dispatch(
        batchApplyRule({ transactionIds: selectedIds, ruleId: selectedRuleId, dryRun: true })
      ).unwrap();
      setPreview(result);
    } catch (err) {
      toast({ title: 'Preview failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedRuleId) return;
    setIsApplying(true);
    try {
      const result = await dispatch(
        batchApplyRule({ transactionIds: selectedIds, ruleId: selectedRuleId, dryRun: false })
      ).unwrap();
      toast({
        title: 'Rule applied',
        description: `Rule matched ${result.affected} of ${result.total} transactions`,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      toast({ title: 'Apply rule failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsApplying(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setSelectedRuleId('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="batch-apply-rule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Apply Rule to {selectedIds.length} Transaction{selectedIds.length !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Select a rule to apply. Preview changes before committing.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4 py-2">
            <Select value={selectedRuleId} onValueChange={setSelectedRuleId}>
              <SelectTrigger data-testid="batch-rule-select">
                <SelectValue placeholder="Select a rule..." />
              </SelectTrigger>
              <SelectContent>
                {rules
                  .filter((r) => r.isActive)
                  .map((rule) => (
                    <SelectItem key={rule._id} value={rule._id}>
                      {rule.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {selectedRule && (
              <div className="p-3 rounded-lg border space-y-2">
                <p className="text-sm font-medium">{selectedRule.name}</p>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">Conditions ({selectedRule.conditionLogic}):</p>
                  {selectedRule.conditions.map((c, i) => (
                    <Badge key={i} variant="secondary" className="text-xs mr-1">
                      {c.field} {c.operator} {String(c.value)}
                    </Badge>
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">Actions:</p>
                  {selectedRule.actions.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-xs mr-1">
                      {a.type}: {String(a.value)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 py-2 max-h-[350px] overflow-y-auto" data-testid="batch-rule-preview">
            <p className="text-sm font-medium">
              {preview.affected} of {preview.total} transactions matched
            </p>
            {isPreviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              preview.results?.slice(0, 50).map((r) => (
                <div key={r.transactionId} className="p-2 rounded border text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium break-words">{r.description}</span>
                    {r.matched ? (
                      <Badge className="text-[10px] bg-green-100 text-green-800">Matched</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">No match</Badge>
                    )}
                  </div>
                  {r.matched && r.actions && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap text-xs text-muted-foreground">
                      {r.actions.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1">
                          <ArrowRight className="h-3 w-3" />
                          <Badge variant="outline" className="text-[10px]">{a.type}</Badge>
                          <span>{String(a.value)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!preview ? (
            <Button onClick={handlePreview} disabled={!selectedRuleId || isPreviewLoading}>
              <Eye className="mr-2 h-4 w-4" />
              {isPreviewLoading ? 'Loading...' : 'Preview'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back
              </Button>
              <Button onClick={handleApply} disabled={isApplying}>
                {isApplying ? 'Applying...' : `Apply Rule`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
